import type { ReactElement, ReactNode } from 'react';
import type {
  CellProps,
  CellState,
  RenderCellProps,
  RenderEditCellProps,
  RenderHeaderCellProps,
  RenderSummaryCellProps,
} from 'components/DataGrid';
import type { FieldConfig } from 'modules/campaign/row';
import type { RowConfig } from 'modules/campaign/row/common';
import type { GridGroup, GridRow, GridSummaryRow, RowOrGroup } from '../../types';
import { defaultScalar, type PlanClipboardCellConfig, type PlanColumn, type ResolvedClipboardCell } from '../clipboard';
import type { GroupPatch } from '../../diff';

export type BranchRenderProps<R extends RowOrGroup> = RenderCellProps<R, GridSummaryRow> & { field: string };

export type BranchEditProps<R extends RowOrGroup> = RenderEditCellProps<R, GridSummaryRow> & { field: string };

/** Bound to a FieldConfig on row.config; cellState comes from row.config[field]. */
export type FieldBranch<R extends GridRow> = {
  // FIXME: tighten to FieldKey<R> once read-only-with-string-field cases
  // (e.g. reach_*, application) are migrated to ReadonlyBranch.
  readonly field: keyof RowConfig | string;
  readonly view: (props: BranchRenderProps<R>) => ReactNode;
  readonly edit?: (props: BranchEditProps<R>) => ReactNode;
  readonly displayCellContent?: boolean;
  /** Clipboard behavior. Absent on an editable branch = default-on (derived
   * from `field`). `false` = opt out entirely. Object = override defaults. */
  readonly clipboard?: PlanClipboardCellConfig<R> | false;
};

/**
 * Group branch — groups carry a single `config.locked`, not per-field FieldConfig.
 * `field` is the editor's write target and the emptiness source; defaults to
 * column.key when omitted.
 */
export type GroupBranch = {
  readonly field?: keyof GridGroup;
  readonly view: (props: BranchRenderProps<GridGroup>) => ReactNode;
  readonly edit?: (props: BranchEditProps<GridGroup>) => ReactNode;
  readonly displayCellContent?: boolean;
  /** Clipboard behavior. Absent on an editable group = default-on. `false` =
   * opt out. Object = override. */
  readonly clipboard?: PlanClipboardCellConfig<GridGroup, GroupPatch> | false;
};

/** Pure read-only — locked, valid, non-empty, non-required. No editor, no field. */
export type ReadonlyBranch<R extends RowOrGroup> = {
  readonly view: (props: BranchRenderProps<R>) => ReactNode;
  readonly displayCellContent?: boolean;
  readonly field?: never;
  readonly edit?: never;
  readonly clipboard?: never;
};

type Branch<R extends RowOrGroup> =
  | (R extends GridRow ? FieldBranch<R> : never)
  | (R extends GridGroup ? GroupBranch : never)
  | ReadonlyBranch<R>;

export const LOCKED_CELL_STATE: CellState = Object.freeze({
  locked: true,
  required: false,
  empty: false,
  valid: true,
});

type ShellArgs = {
  readonly key: string;
  readonly name?: string | ReactElement;
  readonly width?: number;
  readonly frozen?: boolean;
  readonly className?: string;
  readonly headerClassName?: string;
  readonly summaryClassName?: string;
  readonly renderHeader?: (p: RenderHeaderCellProps<RowOrGroup, GridSummaryRow>) => ReactNode;
  readonly renderSummary?: (p: RenderSummaryCellProps<GridSummaryRow, RowOrGroup>) => ReactNode;
};

/**
 * Thin shell wrapper — applies header/summary/width/frozen/className shell
 * args to a caller-supplied per-row `cell` resolver. Use when you need fully
 * custom dispatch; otherwise prefer `byKind`.
 */
export function planColumn(
  args: ShellArgs & {
    readonly cell: (row: RowOrGroup) => CellProps<RowOrGroup, GridSummaryRow>;
    readonly clipboardCell?: PlanColumn['clipboardCell'];
  },
): PlanColumn {
  const {
    key,
    name = '',
    width,
    frozen,
    className,
    headerClassName,
    summaryClassName,
    renderHeader,
    renderSummary,
    cell,
    clipboardCell,
  } = args;

  return {
    key,
    name,
    width,
    minWidth: width,
    maxWidth: width,
    frozen,
    resizable: false,
    sortable: false,
    headerCellClass: headerClassName ?? className,
    summaryCellClass: summaryClassName ?? className,
    renderHeaderCell: renderHeader,
    renderSummaryCell: renderSummary,
    cell,
    clipboardCell,
  };
}

export type ByKindBranches = {
  group?: GroupBranch | ReadonlyBranch<GridGroup>;
  smart?: FieldBranch<GridRow> | ReadonlyBranch<GridRow>;
  media?: FieldBranch<GridRow> | ReadonlyBranch<GridRow>;
  service?: FieldBranch<GridRow> | ReadonlyBranch<GridRow>;
};

export function byKind(args: ShellArgs & { branches: ByKindBranches }): PlanColumn {
  const { branches, ...shell } = args;
  const { key, className } = shell;

  return planColumn({
    ...shell,
    clipboardCell: (row) => {
      if (row._isGroup) {
        const b = branches.group;
        return b === undefined ? null : resolveGroupClipboardCell(row, b, key);
      }
      const b = branches[row.kind.toLowerCase() as 'smart' | 'media' | 'service'];
      return b === undefined ? null : resolveClipboardCell(row, b);
    },
    cell: (row) => {
      const b = pickBranch(row, branches);
      if (!b) return { className, ...LOCKED_CELL_STATE };

      const field = b.field ?? key;

      // Cast at the dispatch boundary: pickBranch selects by row kind, so
      // widening the branch's narrow callbacks back to RowOrGroup is safe —
      // they will only be invoked with the row that selected them.
      const view = b.view as (p: BranchRenderProps<RowOrGroup>) => ReactNode;
      const edit = b.edit as ((p: BranchEditProps<RowOrGroup>) => ReactNode) | undefined;

      return {
        className,
        renderCell: (p) => view({ ...p, field }),
        renderEditCell: edit ? (p) => edit({ ...p, field }) : undefined,
        displayCellContent: b.displayCellContent,
        ...deriveState(row, b, field),
      };
    },
  });
}

function pickBranch(row: RowOrGroup, branches: ByKindBranches): Branch<RowOrGroup> | undefined {
  if (row._isGroup) return branches.group as Branch<RowOrGroup> | undefined;
  const slot = row.kind.toLowerCase() as 'smart' | 'media' | 'service';
  return branches[slot] as Branch<RowOrGroup> | undefined;
}

/** Clipboard behavior of one row cell — an editable branch participates by
 * default (derived from `field`); `clipboard: false` opts out, an object
 * overrides. Groups are handled by resolveGroupClipboardCell. */
function resolveClipboardCell(
  row: GridRow,
  b: FieldBranch<GridRow> | ReadonlyBranch<GridRow>,
): ResolvedClipboardCell | null {
  const { field, clipboard: config } = b;
  if (field === undefined || config === false) return null;
  // Participation is a static, branch-declared signal — NOT `!locked`. Copy must
  // work off a locked row too (copy a locked value, paste into an unlocked
  // sibling); `!locked` only gates paste per-row below.
  if (b.edit === undefined) return null;
  const cfg = config ?? {};

  const value = (row as Record<string, unknown>)[field];
  const pasteable = !deriveState(row, b, field).locked;

  return {
    field,
    scalar: cfg.scalar ? cfg.scalar(row) : defaultScalar(value),
    copy: () => ({ field, value: cfg.copy ? cfg.copy(row) : value }),
    paste: (payload, ctx) => {
      if (!pasteable) return null;
      if (cfg.paste) return cfg.paste(payload, { row, ctx });
      if (cfg.compatible ? !cfg.compatible(payload) : payload.field !== field) return null;
      return { id: row.id, [field]: payload.value };
    },
  };
}

/** Clipboard behavior of one group cell — an editable group participates by
 * default; `clipboard: false` opts out, an object overrides. `field` defaults
 * to the column key. Paste is gated by `group.config.locked`. The group update
 * endpoint expects the full object, so the default paste destructure-merges
 * the new field onto the current row and returns a complete `BudgetGroup`. */
function resolveGroupClipboardCell(
  row: GridGroup,
  b: GroupBranch | ReadonlyBranch<GridGroup>,
  columnKey: string,
): ResolvedClipboardCell<GroupPatch> | null {
  const { clipboard: config } = b;
  if (config === false) return null;
  if (b.edit === undefined) return null;
  const cfg = config ?? {};

  const field = (b.field ?? columnKey) as keyof GridGroup;
  const value = row[field];
  const pasteable = !row.config.locked;

  return {
    field: field as string,
    scalar: cfg.scalar ? cfg.scalar(row) : defaultScalar(value),
    copy: () => ({ field: field as string, value: cfg.copy ? cfg.copy(row) : value }),
    paste: (payload, ctx) => {
      if (!pasteable) return null;
      if (cfg.paste) return cfg.paste(payload, { row, ctx });
      if (cfg.compatible ? !cfg.compatible(payload) : payload.field !== field) return null;
      // group upsert expects the full object — merge the field onto the current group
      return { ...row, [field]: payload.value };
    },
  };
}

function deriveState(row: RowOrGroup, b: Branch<RowOrGroup>, field: string): CellState {
  if (row._isGroup) {
    return {
      locked: row.config.locked || !b.edit,
      required: false,
      // groups carry no per-field FieldConfig — emptiness comes from the value itself
      empty: !!b.edit && !row[field as keyof GridGroup],
      valid: true,
    };
  }

  if (b.field !== undefined) {
    return (row.config as Record<string, FieldConfig | undefined>)[b.field] ?? LOCKED_CELL_STATE;
  }

  return LOCKED_CELL_STATE;
}
